import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Typography, Divider } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { CopyrightFooter } from 'modules/common/components/CopyrightFooter';
import Background from './icons/download_bgimage.jpg';
import { ReactComponent as ZTLogo } from './icons/ZeroTimeLogo.svg';
import { DownloadMacOsApp, DownloadWindowsApp } from './buttons';
import { useOs, OsType } from './hooks';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'absolute',
    height: '100%',
    width: '100%',
    zIndex: 10000,
    backgroundColor: theme.palette.background.paper
  },
  border: {
    paddingTop: theme.spacing(6.25),
    background: '#597CA5',
    height: '100%',
    width: '100%'
  },
  container: {
    borderRadius: theme.spacing(4, 4, 0, 0),
    boxShadow: theme.shadow?.paper,
    background: theme.palette.grey[100],
    display: 'flex',
    height: '100%',
    width: '100%',
    justifyContent: 'space-between',
    flexDirection: 'column'
  },
  topContent: {
    width: '100%',
    backgroundColor: '#f7f7f7'
  },
  centered: {
    height: '600px',
    margin: '0 auto',
    maxWidth: '1224px'
  },
  backgroundImageContainer: {
    backgroundColor: '#f7f7f7',
    background: `url(${Background}) calc(100% + 50px) no-repeat`,
    height: '100%'
  },
  content: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    height: '100%',
    width: '100%'
  },
  buttonContentContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1)
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  logoContent: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    padding: theme.spacing(2, 0)
  },
  leftContent: {
    display: 'flex',
    maxWidth: '45%',
    flexDirection: 'column',
    padding: theme.spacing(5, 0),
    backgroundColor: 'unset'
  },
  buttonContent: {
    display: 'flex',
    padding: theme.spacing(4, 0),
    flexDirection: 'column'
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    '& > a:first-child': {
      marginRight: theme.spacing(2)
    }
  },
  buttonText: {
    marginBottom: theme.spacing(2)
  },
  description: {
    display: 'flex',
    justifyContent: 'center',
    color: theme.palette.common.white,
    backgroundColor: '#110434',
    padding: theme.spacing(5, 2)
  },
  bottomContent: {
    display: 'flex',
    flexDirection: 'column'
  },
  divider: {},
  logoImage: {
    width: theme.spacing(32),
    height: theme.spacing(8),
    marginBottom: theme.spacing(2)
  },
  downloadDescriptionContainer: {
    textAlign: 'center'
  },
  headingText: {
    color: theme.palette.text.primary,
    fontWeight: 600
  },
  descriptionText: {
    fontWeight: 600
  },
  subText: {
    color: theme.palette.text.primary,
    fontWeight: 500
  }
}));

export const DownloadPage = (): JSX.Element => {
  const classes = useStyles();
  const os = useOs();
  return (
    <div data-testid="download-page-root" className={classes.root}>
      <div className={classes.border}>
        <div className={classes.container}>
          <div className={classes.topContent}>
            <div className={classes.centered}>
              <div className={classes.backgroundImageContainer}>
                <div className={classes.leftContent}>
                  <div className={classes.logoContent}>
                    <ZTLogo className={classes.logoImage} />
                    <Typography
                      color="textSecondary"
                      variant={'h3'}
                      className={classes.headingText}
                    >
                      <FormattedMessage id="appMenu.desktopAppDescription" />
                    </Typography>
                  </div>
                  <Divider className={classes.divider} />
                  <div className={classes.buttonContent}>
                    <div className={classes.buttonText}>
                      <Typography variant="body1" className={classes.subText}>
                        <FormattedMessage id="appMenu.getDesktopAppDescription" />
                      </Typography>
                    </div>
                    <div className={classes.buttonContainer}>
                      <DownloadMacOsApp focus={os === OsType.MAC} />
                      <DownloadWindowsApp focus={os === OsType.WINDOWS} />
                    </div>
                  </div>
                  <Divider className={classes.divider} />
                </div>
              </div>
            </div>
            <div className={classes.description}>
              <div className={classes.downloadDescriptionContainer}>
                <Typography variant={'h3'} className={classes.descriptionText}>
                  <FormattedMessage id="appMenu.downloadDescriptionFirstLine" />
                </Typography>
                <Typography variant={'h3'} className={classes.descriptionText}>
                  <FormattedMessage id="appMenu.downloadDescriptionSecondLine" />
                </Typography>{' '}
              </div>
            </div>
          </div>
          <div className={classes.bottomContent}>
            <CopyrightFooter />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;
